/**
 * Maximum demand for a household installation, and the DNO route it points to.
 *
 * THE METHOD. The IET On-Site Guide's allowances for diversity in a household
 * (Appendix A, Table A2), as a surveyor applies them to what they read off the
 * consumer unit:
 *
 *   - lighting: 66% of the total of the lighting circuits;
 *   - socket circuits: 100% of the largest, 40% of every other;
 *   - cookers: 10 A plus 30% of the rest of each cooker's rating;
 *   - instantaneous water heaters (electric showers): 100% of the largest,
 *     100% of the second, 25% of the rest;
 *   - storage water heaters (immersions), heat pumps, EV chargers and
 *     anything else: 100% — no diversity.
 *
 * Each circuit counts at what it draws (`loadA`) when the surveyor knows it,
 * else at its breaker's rating. The result is a design estimate, and says so;
 * a measured peak from a clamp meter, where one was taken, is shown beside it.
 *
 * THE ROUTE. Under the Energy Networks Association's connect-and-notify process
 * a heat pump may be connected and the DNO told afterwards (within 28 days)
 * when the house's maximum demand, the heat pump included, is within the
 * supply's main fuse, and nothing about the supply needs the DNO first. A
 * looped supply, a safety concern, or a demand over the fuse means applying to
 * the DNO before the work. The DNO's own rules decide; this is the guide.
 */

/** One consumer-unit circuit — the same shape as `@sts/schemas`' site-survey `Circuit`. */
export interface Circuit {
  readonly name: string;
  readonly kind: "sockets" | "lighting" | "cooker" | "shower" | "immersion" | "heat-pump" | "ev-charger" | "other";
  readonly ratingA: number;
  readonly loadA?: number;
}

export interface DemandLine {
  readonly name: string;
  readonly kind: Circuit["kind"];
  /** What the circuit counts for after diversity, A. */
  readonly demandA: number;
  /** In words: "40% of 32 A". */
  readonly rule: string;
}

export type DnoRoute = "connect-and-notify" | "apply-first" | "unknown";

export interface MaximumDemand {
  readonly lines: readonly DemandLine[];
  /** Everything but the heat pump, after diversity, A. */
  readonly existingA: number;
  /** The heat pump circuit(s), A. */
  readonly heatPumpA: number;
  readonly totalA: number;
  /** The main fuse, A; null when it is not known. */
  readonly fuseA: number | null;
  /** How much of the fuse the total uses, 0–1+; null without a fuse. */
  readonly fuseShare: number | null;
  /** Within the fuse, but by less than 10% of it. */
  readonly tight: boolean;
  readonly route: DnoRoute;
  /** Why the route is what it is, one sentence each. */
  readonly reasons: readonly string[];
}

export interface MaximumDemandInput {
  readonly circuits: readonly Circuit[];
  /** The main fuse, A, or null when unknown. */
  readonly fuseA: number | null;
  readonly looped?: boolean;
  readonly safetyConcern?: boolean;
}

const round1 = (n: number): number => Math.round(n * 10) / 10;

function draw(c: Circuit): number {
  return c.loadA ?? c.ratingA;
}

export function maximumDemand(input: MaximumDemandInput): MaximumDemand {
  const lines: DemandLine[] = [];
  const byKind = (kind: Circuit["kind"]): Circuit[] => input.circuits.filter((c) => c.kind === kind);

  // Lighting: 66% of the lot.
  for (const c of byKind("lighting")) {
    lines.push({ name: c.name, kind: c.kind, demandA: round1(draw(c) * 0.66), rule: `66% of ${draw(c)} A` });
  }

  // Sockets: the largest at 100%, the rest at 40%.
  const sockets = byKind("sockets").sort((a, b) => draw(b) - draw(a));
  sockets.forEach((c, i) => {
    const share = i === 0 ? 1 : 0.4;
    lines.push({
      name: c.name,
      kind: c.kind,
      demandA: round1(draw(c) * share),
      rule: i === 0 ? `100% of ${draw(c)} A (largest socket circuit)` : `40% of ${draw(c)} A`,
    });
  });

  // Cookers: 10 A plus 30% of the rest.
  for (const c of byKind("cooker")) {
    const a = draw(c);
    const demand = a <= 10 ? a : 10 + 0.3 * (a - 10);
    lines.push({ name: c.name, kind: c.kind, demandA: round1(demand), rule: a <= 10 ? `${a} A` : `10 A + 30% of ${round1(a - 10)} A` });
  }

  // Showers: the two largest in full, the rest at 25%.
  const showers = byKind("shower").sort((a, b) => draw(b) - draw(a));
  showers.forEach((c, i) => {
    const share = i < 2 ? 1 : 0.25;
    lines.push({ name: c.name, kind: c.kind, demandA: round1(draw(c) * share), rule: i < 2 ? `100% of ${draw(c)} A` : `25% of ${draw(c)} A` });
  });

  // No diversity.
  for (const kind of ["immersion", "ev-charger", "other", "heat-pump"] as const) {
    for (const c of byKind(kind)) {
      lines.push({ name: c.name, kind: c.kind, demandA: round1(draw(c)), rule: `${draw(c)} A, no diversity` });
    }
  }

  const heatPumpA = round1(lines.filter((l) => l.kind === "heat-pump").reduce((s, l) => s + l.demandA, 0));
  const totalA = round1(lines.reduce((s, l) => s + l.demandA, 0));
  const existingA = round1(totalA - heatPumpA);
  const fuseA = input.fuseA;
  const fuseShare = fuseA ? totalA / fuseA : null;

  const reasons: string[] = [];
  let route: DnoRoute;
  if (input.looped) reasons.push("The supply is looped: the DNO has to unloop it before a heat pump goes on.");
  if (input.safetyConcern) reasons.push("There is a safety concern with the DNO's equipment: report it to them first.");
  if (heatPumpA === 0) reasons.push("No heat pump circuit is listed yet: add it with its running current from the datasheet.");
  if (fuseA === null) {
    reasons.push("The main fuse rating is not known: read it off the cut-out.");
    route = input.looped || input.safetyConcern ? "apply-first" : "unknown";
  } else if (totalA > fuseA) {
    reasons.push(`The maximum demand, ${totalA} A, is over the ${fuseA} A main fuse: apply to the DNO before the work.`);
    route = "apply-first";
  } else if (input.looped || input.safetyConcern) {
    route = "apply-first";
  } else {
    reasons.push(`The maximum demand, ${totalA} A, is within the ${fuseA} A main fuse: connect, then notify the DNO within 28 days.`);
    if (totalA > fuseA * 0.9) {
      reasons.push("It is within 10% of the fuse, though: a measured peak from a clamp meter would confirm it.");
    }
    route = heatPumpA === 0 ? "unknown" : "connect-and-notify";
  }

  const tight = fuseA !== null && totalA <= fuseA && totalA > fuseA * 0.9;
  return { lines, existingA, heatPumpA, totalA, fuseA, fuseShare, tight, route, reasons };
}
